import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";

import { usePrevious } from "@uidotdev/usehooks";
import { SidebarModalPanel } from "@vega-core/components/Modals/SidebarModal/SidebarModalPanel";
import { SidebarModalRoot } from "@vega-core/components/Modals/SidebarModal/SidebarModalRoot";
import { Key } from "@vega-core/constants/global";
import { PageScrollPrevent } from "@vega-core/utils/ScrollPreventHelper";

const TRANSITION_TIME = 150;

type Props = {
  hideCloseButton?: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  noPadding?: boolean;
  noScrolling?: boolean;
  keepPreviousState?: boolean;
  customWidthClassnames?: string;
  dataTestId: string;
};

export function SidebarModal({
  children,
  hideCloseButton = false,
  isOpen,
  setIsOpen,
  noPadding = false,
  noScrolling = false,
  keepPreviousState = true,
  customWidthClassnames,
  dataTestId = "",
}: PropsWithChildren<Props>) {
  const [isClosing, setIsClosing] = useState(false);
  const isOpenPrev = usePrevious(isOpen);

  const handleClose = useCallback(() => {
    if (isClosing || !isOpen) return;

    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, TRANSITION_TIME);
  }, [isOpen, isClosing, setIsOpen, setIsClosing]);

  const onOpen = useCallback(() => {
    PageScrollPrevent.enable();
  }, []);

  const onCloseCleanup = useCallback(() => {
    PageScrollPrevent.disable();
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (!(isOpenPrev && keepPreviousState)) {
        onOpen();
      }
    } else if (isOpenPrev) {
      onCloseCleanup();
    }

    return () => {
      if (isOpen) {
        onCloseCleanup();
      }
    };
  }, [isOpen, isOpenPrev, keepPreviousState, onOpen, onCloseCleanup]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === Key.Escape) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleClose]);

  return (
    <SidebarModalRoot
      isClosing={isClosing}
      isOpen={isOpen}
      onClose={handleClose}
    >
      <SidebarModalPanel
        customWidthClassnames={customWidthClassnames}
        dataTestId={dataTestId}
        hideCloseButton={hideCloseButton}
        isClosing={isClosing}
        isOpen={isOpen}
        noPadding={noPadding}
        noScrolling={noScrolling}
        onClose={handleClose}
      >
        {children}
      </SidebarModalPanel>
    </SidebarModalRoot>
  );
}
